import {
  Skeleton,
  SkeletonItem,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

export type ContentSkeletonVariant = 'form' | 'list' | 'lines';

export interface ContentSkeletonProps {
  variant?: ContentSkeletonVariant;
  rows?: number;
  label?: string;
  className?: string;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '100%',
    boxSizing: 'border-box',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  listRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  listText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  lines: {
    gap: tokens.spacingVerticalS,
  },
  tile: {
    width: '34px',
    height: '34px',
  },
  label: {
    width: '30%',
    maxWidth: '140px',
  },
  textLong: {
    width: '70%',
  },
  textShort: {
    width: '45%',
  },
});

const DEFAULT_ROWS = { form: 4, list: 3, lines: 3 } as const;

const FormRow = () => {
  const styles = useStyles();
  return (
    <div className={styles.field}>
      <SkeletonItem size={12} className={styles.label} />
      <SkeletonItem size={32} />
    </div>
  );
};

const ListRow = () => {
  const styles = useStyles();
  return (
    <div className={styles.listRow}>
      <SkeletonItem shape="square" size={36} className={styles.tile} />
      <div className={styles.listText}>
        <SkeletonItem size={16} className={styles.textLong} />
        <SkeletonItem size={12} className={styles.textShort} />
      </div>
    </div>
  );
};

const LineRow = ({ index }: { index: number }) => {
  const styles = useStyles();
  return (
    <SkeletonItem
      size={16}
      className={index % 2 === 0 ? styles.textLong : undefined}
    />
  );
};

const rowByVariant = {
  form: () => <FormRow />,
  list: () => <ListRow />,
  lines: (index: number) => <LineRow index={index} />,
} as const;

const ContentSkeleton = ({
  variant = 'form',
  rows = DEFAULT_ROWS[variant],
  label,
  className,
}: ContentSkeletonProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const renderRow = rowByVariant[variant];
  return (
    <Skeleton
      aria-label={label ?? t('loading', 'Loading...')}
      className={mergeClasses(
        styles.root,
        variant === 'lines' && styles.lines,
        className
      )}
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index}>{renderRow(index)}</div>
      ))}
    </Skeleton>
  );
};

export default ContentSkeleton;
